import { useFrotista } from 'modules/user/hooks/useFrotista'
import React, { createContext, useCallback, useState, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import ENaturezaJuridica from '../domain/entities/ENaturezaJuridica'
import ETipoDocumentoOnboarding from '../domain/entities/ETipoDocumentoOnboarding'
import { ICreateContaPJOnboardingRequest } from '../domain/entities/ICreateContaPJOnboardingRequest'
import { IDocumentoContaPJOnboardingRequest } from '../domain/entities/IDocumentoContaPJOnboardingRequest'
import TipoEmpresa from '../domain/entities/TipoEmpresa'

import PagamentoRoutes from '../utils/PagamentoRoutes'

export enum EStepOnboarding {
  DadosGerais = 0,
  Representante = 1,
  Documentos = 2,
  Validacao = 3,
}
interface IOnboardingContextState {
  naturezaJuridicaEmpresaControlada: ENaturezaJuridica
  setNaturezaJuridicaEmpresaControlada: (value: ENaturezaJuridica) => void
  empresaControlada: boolean
  setEmpresaControlada: (value: boolean) => void
  contaPj: ICreateContaPJOnboardingRequest
  setContaPj: (value: ICreateContaPJOnboardingRequest) => void
  getNaturezaJuridica: () => ENaturezaJuridica
  documentos: IDocumentoContaPJOnboardingRequest[]
  getDocumentoNome: (tipo: ETipoDocumentoOnboarding) => string
  addDocumento: (value: IDocumentoContaPJOnboardingRequest) => void
  removeDocumento: (tipo: ETipoDocumentoOnboarding) => void
  removeDocumentoRepresentantes: () => void
  removeDocumentosEmpresa: () => void
  documentosEmpresaControladora: IDocumentoContaPJOnboardingRequest[]
  getDocumentoEmpresaControladoraNome: (
    tipo: ETipoDocumentoOnboarding,
  ) => string
  addDocumentoEmpresaControladora: (
    value: IDocumentoContaPJOnboardingRequest,
  ) => void
  removeDocumentoEmpresaControladora: (tipo: ETipoDocumentoOnboarding) => void
  success: boolean
  setSuccess: (success: boolean) => void
  showPercentualTransferencia: boolean
  setShowPercentualTransferencia: (showPercentualTransferencia: boolean) => void
  step: EStepOnboarding
  setStep: (value: EStepOnboarding) => void
  numberOfSteps: number
  handleNextButton: () => void
  handleBackButton: () => void
  clear: () => void
}

export const OnboardingContext = createContext<IOnboardingContextState>(
  {} as IOnboardingContextState,
)

export function useOnboarding(): IOnboardingContextState {
  const context = useContext(OnboardingContext)

  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }

  return context
}

export const OnboardingProvider: React.FC = ({ children }) => {
  const history = useHistory()
  const { frotista } = useFrotista()
  const [step, setStep] = useState<EStepOnboarding>(EStepOnboarding.DadosGerais)
  const [
    naturezaJuridicaEmpresaControlada,
    setNaturezaJuridicaEmpresaControlada,
  ] = useState<ENaturezaJuridica>(ENaturezaJuridica.LTDA)
  const [empresaControlada, setEmpresaControlada] = useState(false)
  const [contaPj, setContaPj] = useState<ICreateContaPJOnboardingRequest>({
    frotista_id: 0,
    receita_anual: 0,
    responsaveis: {
      pessoas_fisicas: [],
      pessoas_juridicas: [],
    },
  })
  const [documentos, setDocumentos] = useState<
    IDocumentoContaPJOnboardingRequest[]
  >([])
  const [documentosEmpresaControladora, setDocumentosEmpresaControladora] =
    useState<IDocumentoContaPJOnboardingRequest[]>([])
  const [success, setSuccess] = useState(false)
  const [showPercentualTransferencia, setShowPercentualTransferencia] =
    useState(false)
  const tipoEmpresa = new TipoEmpresa()

  const numberOfSteps =
    Object.keys({
      DadosEmpresa: 0,
      Representante: 1,
      Documentos: 2,
      Validacao: 3,
    }).length - 1

  const handleNextButtonValue = () => {
    setStep(step => {
      const newStep = step + 1

      if (newStep > numberOfSteps) {
        return step
      }

      return newStep
    })
  }

  const setStepValue = (value: EStepOnboarding) => {
    setStep(value)
  }

  const clear = () => {
    setDocumentos([])
    setDocumentosEmpresaControladora([])
    setNaturezaJuridicaEmpresaControlada(null)
    setEmpresaControlada(null)
    setContaPj(null)
    setSuccess(false)
    setShowPercentualTransferencia(false)
    setStep(EStepOnboarding.DadosGerais)
  }

  const handleNaturezaJuridica = useCallback(() => {
    if (tipoEmpresa.isMEI(frotista.natureza_juridica))
      return ENaturezaJuridica.MEI
    else if (tipoEmpresa.isSA(frotista.natureza_juridica))
      return ENaturezaJuridica.SA
    else return ENaturezaJuridica.LTDA
  }, [frotista, step])

  const removeDocumento = useCallback(
    (tipo: ETipoDocumentoOnboarding) => {
      const result = documentos?.find(
        (doc: IDocumentoContaPJOnboardingRequest) => doc.tipo === tipo,
      )
      const index = documentos?.indexOf(result)

      if (index >= 0) {
        documentos.splice(index, 1)
      }
      setDocumentos(documentos)
    },
    [documentos],
  )

  const removeDocumentoRepresentantes = useCallback(() => {
    removeDocumento(ETipoDocumentoOnboarding.CNH)
    removeDocumento(ETipoDocumentoOnboarding.SELFIE)
    removeDocumento(ETipoDocumentoOnboarding.PROCURACAO)
  }, [])

  const removeDocumentosEmpresa = useCallback(() => {
    removeDocumento(ETipoDocumentoOnboarding.ESTATUTO_SOCIAL)
  }, [])

  const handleBackButtonValue = () => {
    setStep(step => {
      const newStep = step - 1

      if (
        newStep === EStepOnboarding.Documentos &&
        handleNaturezaJuridica() === ENaturezaJuridica.MEI
      ) {
        removeDocumentoRepresentantes()
        return EStepOnboarding.Representante
      }

      if (newStep < 0) {
        clear()
        history.push(PagamentoRoutes.PAGAMENTO, {
          reloadHomePagamento: false,
        })
        return 0
      }

      return step - 1
    })
  }

  const setContaPjValue = useCallback(
    (value: ICreateContaPJOnboardingRequest) => {
      setContaPj(value)
    },
    [],
  )

  const handleSetNaturezaJuridicaEmpresaControlada = useCallback(
    (value: ENaturezaJuridica) => {
      setNaturezaJuridicaEmpresaControlada(value)
    },
    [naturezaJuridicaEmpresaControlada],
  )

  const handleSetEmpresaControlada = useCallback(
    (value: boolean) => {
      if (!value) {
        setDocumentosEmpresaControladora(null)
      }
      setEmpresaControlada(value)
    },
    [empresaControlada],
  )

  const getDocumentoNome = useCallback(
    (tipo: ETipoDocumentoOnboarding) => {
      const documento = documentos?.find(
        (doc: IDocumentoContaPJOnboardingRequest) => doc?.tipo === tipo,
      )
      return documento?.arquivo?.name
    },
    [documentos],
  )

  const addDocumento = useCallback(
    (documento: IDocumentoContaPJOnboardingRequest) => {
      const result = documentos?.find(
        (doc: IDocumentoContaPJOnboardingRequest) =>
          doc.tipo === documento.tipo,
      )
      const index = documentos?.indexOf(result)
      if (index >= 0) {
        documentos[index] = documento
      } else {
        documentos.push(documento)
      }
      setDocumentos(documentos)
    },
    [documentos],
  )

  const getDocumentoEmpresaControladoraNome = useCallback(
    (tipo: ETipoDocumentoOnboarding) => {
      const documentoEmpresaControladora = documentosEmpresaControladora?.find(
        (doc: IDocumentoContaPJOnboardingRequest) => doc?.tipo === tipo,
      )
      return documentoEmpresaControladora?.arquivo?.name
    },
    [documentosEmpresaControladora],
  )

  const addDocumentoEmpresaControladora = useCallback(
    (documento: IDocumentoContaPJOnboardingRequest) => {
      const result = documentosEmpresaControladora.find(
        (doc: IDocumentoContaPJOnboardingRequest) =>
          doc.tipo === documento.tipo,
      )
      const index = documentosEmpresaControladora.indexOf(result)
      if (index >= 0) {
        documentosEmpresaControladora[index] = documento
      } else {
        documentosEmpresaControladora.push(documento)
      }
      setDocumentosEmpresaControladora(documentosEmpresaControladora)
    },
    [documentosEmpresaControladora],
  )

  const removeDocumentoEmpresaControladora = useCallback(
    (tipo: ETipoDocumentoOnboarding) => {
      const result = documentosEmpresaControladora.find(
        (doc: IDocumentoContaPJOnboardingRequest) => doc.tipo === tipo,
      )
      const index = documentosEmpresaControladora.indexOf(result)

      if (index >= 0) {
        documentosEmpresaControladora.splice(index, 1)
      }
      setDocumentosEmpresaControladora(documentosEmpresaControladora)
    },

    [documentosEmpresaControladora],
  )

  const handleSuccess = useCallback((success: boolean) => {
    setSuccess(success)
  }, [])

  const handleShowPercentualTransferencia = useCallback(
    (showPercentualTransferencia: boolean) => {
      setShowPercentualTransferencia(showPercentualTransferencia)
    },
    [],
  )

  return (
    <OnboardingContext.Provider
      value={{
        step,
        setStep: setStepValue,
        numberOfSteps,
        handleNextButton: handleNextButtonValue,
        handleBackButton: handleBackButtonValue,
        naturezaJuridicaEmpresaControlada,
        setNaturezaJuridicaEmpresaControlada:
          handleSetNaturezaJuridicaEmpresaControlada,
        getNaturezaJuridica: handleNaturezaJuridica,
        empresaControlada,
        setEmpresaControlada: handleSetEmpresaControlada,
        contaPj,
        setContaPj: setContaPjValue,
        documentos,
        getDocumentoNome,
        addDocumento,
        removeDocumento,
        removeDocumentoRepresentantes,
        removeDocumentosEmpresa,
        documentosEmpresaControladora,
        getDocumentoEmpresaControladoraNome,
        addDocumentoEmpresaControladora,
        removeDocumentoEmpresaControladora,
        success,
        setSuccess: handleSuccess,
        showPercentualTransferencia,
        setShowPercentualTransferencia: handleShowPercentualTransferencia,
        clear,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}
